/** @jsx jsx */
import React, { useMemo } from 'react';
import { jsx } from '@compiled/react';
import { Box } from '@atlaskit/primitives/compiled';
import { CommentAction } from '@atlaskit/comment';
import AkToolTip from '@atlaskit/tooltip';
import { isJsmLoomExperimentEnabled } from '@atlassian/jira-loom-jsm-agent-response/src/common/utils.tsx';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import draftMessages from '@atlassian/jira-issue-view-common/src/messages/drafts.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import messages from '../messages.tsx';
import { useCommentContext } from './CommentContextProvider.tsx';
import { useEdit } from './utils.tsx';

type Props = {
	onClick: (
		event: React.MouseEvent<HTMLElement>,
		analyticsEvent?: UIAnalyticsEvent,
		isSiteAdmin?: boolean,
	) => void;
};
const EditActionComponent = ({ onClick }: Props) => {
	const { hasDraft, isSaving, isThirdPartySyncOn } = useCommentContext();
	const { formatMessage } = useIntl();

	if (isThirdPartySyncOn && fg('jira_issue_view_show_slack_sync_info')) {
		return (
			<Box>
				<AkToolTip content={formatMessage(messages.disabledEditTooltip)}>
					<CommentAction key="edit" onClick={onClick} isDisabled>
						{formatMessage(messages.edit)}
						{hasDraft ? ` (${formatMessage(draftMessages.draftIndicator)})` : ''}
					</CommentAction>
				</AkToolTip>
			</Box>
		);
	}

	return (
		<Box>
			<CommentAction key="edit" onClick={onClick} isDisabled={isSaving}>
				{formatMessage(messages.edit)}
				{hasDraft ? ` (${formatMessage(draftMessages.draftIndicator)})` : ''}
			</CommentAction>
		</Box>
	);
};
const EditAction = () => {
	const { canEdit, hasFloatingMenu } = useCommentContext();

	const onEditClick = useEdit();
	const jsmLoomExperimentEnabled = useMemo(() => isJsmLoomExperimentEnabled(), []);

	if (!canEdit || hasFloatingMenu) {
		return null;
	}
	if (jsmLoomExperimentEnabled.enabled) {
		return (
			<TenantContextSubscriber>
				{({ tenantContext: { isSiteAdmin } }) => (
					<EditActionComponent
						onClick={(event, analyticsEvent) => onEditClick(event, analyticsEvent, isSiteAdmin)}
					/>
				)}
			</TenantContextSubscriber>
		);
	}
	return <EditActionComponent onClick={onEditClick} />;
};
export default EditAction;
